import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, EmptyState, ListGroup, ListRow, Skeleton, TextField } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import { usePrefs } from '../prefs.tsx';
import { haptic } from '../telegram.ts';
import { FilterChips, Pager, providerName, useDebounced } from './common.tsx';

type Activity = 'all' | 'enabled' | 'disabled';

const ACTIVITY: { value: Activity; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'enabled', label: 'Активные' },
  { value: 'disabled', label: 'Отключённые' },
];

function originalMoney(minor: number, currency: string) {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

/** Полный каталог синхронизированных облачных тарифов с техническими идентификаторами. */
export function AdminServerCatalogScreen() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { money, dateTime } = usePrefs();
  const [provider, setProvider] = useState('all');
  const [activity, setActivity] = useState<Activity>('all');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const settledSearch = useDebounced(search);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const catalog = useQuery({
    ...trpc.admin.serverPlans.queryOptions({
      provider: provider === 'all' ? undefined : provider,
      enabled: activity === 'all' ? undefined : activity === 'enabled',
      search: settledSearch,
      offset,
    }),
    placeholderData: (prev) => prev,
  });
  const sync = useMutation(
    trpc.admin.syncServerPlans.mutationOptions({
      onSuccess: async ({ providers }) => {
        haptic.success();
        const updated = providers.filter((provider) => provider.ok);
        const failed = providers.filter((provider) => !provider.ok);
        const plans = updated.reduce((sum, provider) => sum + provider.upserted, 0);
        setSyncResult(
          failed.length
            ? `Обновлено тарифов: ${plans}. Не ответили: ${failed.map((provider) => providerName(provider.provider)).join(', ')}.`
            : `Каталог обновлён: ${plans} тарифов от ${updated.length} провайдеров.`,
        );
        await queryClient.invalidateQueries(trpc.admin.serverPlans.pathFilter());
      },
      onError: () => haptic.error(),
    }),
  );

  const changeFilter = (fn: () => void) => {
    fn();
    setOffset(0);
  };
  const providerOptions = [
    { value: 'all', label: 'Все поставщики' },
    ...(catalog.data?.providers ?? []).map((p) => ({
      value: p.provider,
      label: `${providerName(p.provider)} · ${p.total}`,
    })),
  ];

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">Каталог серверов</h1>
      <p className="ul-group__footer">
        Тарифы, загруженные из облаков. Внутренний ID использует Uplink, upstream ID — API поставщика.
      </p>
      <Button
        variant="secondary"
        icon="reboot"
        loading={sync.isPending}
        onClick={() => {
          setSyncResult(null);
          sync.mutate();
        }}
      >
        Обновить каталог
      </Button>
      {syncResult ? (
        <div className="ul-success-note" role="status">
          {syncResult}
        </div>
      ) : null}
      {sync.isError ? (
        <div className="ul-error" role="alert">
          {errorText(sync.error)}
        </div>
      ) : null}
      <TextField
        id="server-plan-search"
        label="Поиск"
        value={search}
        onChange={(value) => changeFilter(() => setSearch(value))}
        placeholder="ID тарифа или локация"
      />
      <FilterChips
        options={providerOptions}
        value={provider}
        onChange={(value) => changeFilter(() => setProvider(value))}
      />
      <FilterChips
        options={ACTIVITY}
        value={activity}
        onChange={(value) => changeFilter(() => setActivity(value))}
      />

      {catalog.isPending ? (
        <Skeleton height={360} />
      ) : catalog.isError ? (
        <div className="ul-error" role="alert">
          {errorText(catalog.error)}
        </div>
      ) : catalog.data.rows.length === 0 ? (
        <EmptyState icon="server" title="Тарифов не найдено" text="Измените фильтры или строку поиска" />
      ) : (
        <div className={catalog.isPlaceholderData ? 'ul-results ul-results--stale' : 'ul-results'}>
          <div className="ul-multiline">
            <ListGroup header={`Найдено ${catalog.data.total}`}>
              {catalog.data.rows.map((p) => (
                <ListRow
                  key={p.id}
                  icon="server"
                  title={`${providerName(p.provider)} · ${p.location}`}
                  subtitle={[
                    `Внутренний ID: ${p.id}`,
                    `Upstream ID: ${p.providerPlanId}`,
                    `${p.cpu} vCPU · ${Math.round(p.ramMb / 1024)} ГБ RAM · ${p.diskGb} ГБ ${p.diskType}`,
                    `Себестоимость: ${originalMoney(p.costOriginal, p.costCurrency)} (≈ ${money(p.costMonthly)})`,
                    `Цена продажи: ${money(p.priceMonthly)} · обновлён ${dateTime(p.updatedAt)}`,
                  ].join('\n')}
                  trailing={
                    <Badge tone={p.enabled ? 'ok' : 'neutral'}>{p.enabled ? 'активен' : 'выключен'}</Badge>
                  }
                />
              ))}
            </ListGroup>
          </div>
          <Pager offset={offset} total={catalog.data.total} onChange={setOffset} />
        </div>
      )}
    </main>
  );
}
